import { apiClient, adminApiClient } from '../axios.config';
import { API_ENDPOINTS } from '../constants';
import type { ApiResponse } from '../types/common';
import type {
  GetAllCharactersResponse,
  GetCharactersParams,
  GetVotingCharactersResponse,
  SubmitVoteRequest,
  CreateCharacterRequest,
  GetVotingStatsResponse,
} from '../types/character.types';

/**
 * Character Service - All character-related API calls
 */
export const characterService = {
  /**
   * Get all available characters with optional filters
   * Returns only active and enabled characters for user chat selection
   */
  getAllCharacters: async (params?: GetCharactersParams): Promise<ApiResponse<GetAllCharactersResponse>> => {
    const response = await apiClient.get<ApiResponse<GetAllCharactersResponse>>(
      API_ENDPOINTS.CHARACTER.GET_ALL,
      { params }
    );
    return response.data;
  },

  /**
   * Get characters in voting phase with vote stats
   */
  getVotingCharacters: async (): Promise<ApiResponse<GetVotingCharactersResponse>> => {
    const response = await apiClient.get<ApiResponse<GetVotingCharactersResponse>>(
      API_ENDPOINTS.CHARACTER.VOTING
    );
    return response.data;
  },

  /**
   * Submit a vote (yes/no) for a character in voting
   */
  submitVote: async (request: SubmitVoteRequest): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.CHARACTER.VOTE,
      request
    );
    return response.data;
  },

  /**
   * Create a new character (admin only)
   */
  createCharacter: async (request: CreateCharacterRequest): Promise<ApiResponse<{ _id: string }>> => {
    const response = await adminApiClient.post<ApiResponse<{ _id: string }>>(
      API_ENDPOINTS.CHARACTER.GET_ALL,
      request
    );
    return response.data;
  },

  /**
   * Get voting stats (admin only)
   */
  getVotingStats: async (): Promise<ApiResponse<GetVotingStatsResponse>> => {
    const response = await adminApiClient.get<ApiResponse<GetVotingStatsResponse>>(
      API_ENDPOINTS.CHARACTER.VOTING_STATS
    );
    return response.data;
  },

  /**
   * Toggle voting on/off (admin only)
   */
  toggleVoting: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await adminApiClient.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.CHARACTER.VOTING_TOGGLE
    );
    return response.data;
  },

  /**
   * Finalize voting (admin only)
   */
  finalizeVoting: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await adminApiClient.post<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.CHARACTER.VOTING_FINALIZE
    );
    return response.data;
  },
};
